import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import './CommandPalette.css';

function useDebounce(value: string, delay = 350): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export interface CommandPaletteProps<T> {
  show: boolean;
  onHide: () => void;
  /** Called with the debounced search string whenever it changes. */
  onSearch: (termo: string) => void;
  /** Data to display. Should already reflect the latest `onSearch` call. */
  items: T[];
  isFetching?: boolean;
  isSuccess?: boolean;
  /** Called when the user confirms a selection (click or Enter). */
  onSelect: (item: T) => void;
  /** Called after the modal close animation fully completes. */
  onExited?: () => void;
  /** Stable key for each item (used as React key). */
  getKey: (item: T) => string | number;
  /**
   * Render the inner content of a row. The wrapper div (.cmd-row) and
   * focus/hover handling are provided by CommandPalette.
   */
  renderRow: (item: T) => React.ReactNode;
  placeholder?: string;
  emptyMessage?: string;
  /**
   * Minimum characters before results are shown.
   * Set to 0 (default) to show all results when the palette opens.
   */
  minChars?: number;
}

function CommandPalette<T>({
  show,
  onHide,
  onSearch,
  items,
  isFetching,
  isSuccess,
  onSelect,
  getKey,
  renderRow,
  placeholder = 'Buscar...',
  emptyMessage = 'Nenhum resultado encontrado',
  minChars = 0,
  onExited,
}: CommandPaletteProps<T>) {
  const [termo, setTermo] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const debouncedTermo = useDebounce(termo);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset and focus when modal opens
  useEffect(() => {
    if (show) {
      setTermo('');
      setFocusedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [show]);

  // Notify parent of debounced search changes
  useEffect(() => {
    onSearch(debouncedTermo);
  // onSearch identity must be stable (useCallback in parent) to avoid loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTermo]);

  // Auto-focus first result when list changes
  useEffect(() => {
    setFocusedIndex(items.length > 0 ? 0 : -1);
  }, [items.length]);

  // Scroll focused row into view
  useEffect(() => {
    if (focusedIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[focusedIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  const handleSelect = useCallback((item: T) => {
    onSelect(item);
    onHide();
  }, [onSelect, onHide]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(i => Math.min(i + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && items[focusedIndex]) handleSelect(items[focusedIndex]);
        break;
      case 'Escape':
        onHide();
        break;
    }
  };

  const belowMinChars = debouncedTermo.length < minChars;
  const showEmpty = isSuccess && !belowMinChars && items.length === 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      dialogClassName="cmd-modal-dialog"
      backdropClassName="cmd-backdrop"
      contentClassName="cmd-dialog border-0"
      restoreFocus={false}
      onExited={onExited}
    >
      <div className="cmd-search-row">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="cmd-search-icon" />
        <input
          ref={inputRef}
          className="cmd-input"
          type="text"
          value={termo}
          onChange={e => setTermo(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
        />
        <button className="cmd-close" onClick={onHide} type="button">esc</button>
      </div>

      <div className="cmd-results" ref={listRef}>
        {isFetching && (
          <div className="cmd-spinner">
            <Spinner size="sm" animation="border" variant="secondary" />
          </div>
        )}
        {!isFetching && belowMinChars && (
          <div className="cmd-empty">
            Digite pelo menos {minChars} {minChars === 1 ? 'caractere' : 'caracteres'} para buscar
          </div>
        )}
        {!isFetching && showEmpty && (
          <div className="cmd-empty">{emptyMessage}</div>
        )}
        {items.map((item, i) => (
          <div
            key={getKey(item)}
            className={`cmd-row${focusedIndex === i ? ' cmd-row--focused' : ''}`}
            role="button"
            tabIndex={-1}
            onClick={() => handleSelect(item)}
            onMouseEnter={() => setFocusedIndex(i)}
          >
            {renderRow(item)}
          </div>
        ))}
      </div>

      <div className="cmd-footer">
        <span className="cmd-hint"><kbd className="cmd-key">↑↓</kbd> navegar</span>
        <span className="cmd-hint"><kbd className="cmd-key">↵</kbd> selecionar</span>
        <span className="cmd-hint"><kbd className="cmd-key">esc</kbd> fechar</span>
      </div>
    </Modal>
  );
}

export default CommandPalette;
